package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initAKotlinServerThatShipsTwicePage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/AKotlinServerThatShipsTwice.md", FrontMatterElement.Builder { addScalar("title", "A Kotlin server that ships twice, in an hour"); addScalar("searchTitle", "Kotlin/Native and JVM from one source — a template with measured numbers"); addScalar("date", "2026-09-16"); addScalar("description", "Clone a template, rename it, and you have a service that compiles to a native Linux binary and a JVM distribution from one source: two probes-and-shutdown halves that behave the same, a 14 MB image, and 3 min 48 s from git clone to both answering. Every number here was measured on a machine that had never seen the repository."); }))
}

@Page("/blog/a-kotlin-server-that-ships-twice")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun AKotlinServerThatShipsTwicePage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every Kotlin service I start needs the same day of work before it does anything: two targets that both run, a store that works on both, a shutdown that finishes the request it is holding, three health endpoints that answer three different questions, an image, and enough CI to tell whether any of it is true. None of that is the product. All of it is the part you get wrong quietly.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/keel") {
            org.jetbrains.compose.web.dom.Text("keel")
        }
        org.jetbrains.compose.web.dom.Text(" is that day, done once. It is a GitHub template: clone it, rename it, and you have a service with one route, one table and one entity — and two artefacts that ship.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""git clone https://github.com/youndie/keel my-service && cd my-service
./gradlew build
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Renaming is genuinely all that is left. The first service built from it — a webhook relay — came to ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("249 lines of renaming across 32 files")
        }
        org.jetbrains.compose.web.dom.Text(", every one a substitution, against ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("178 lines of its own domain")
        }
        org.jetbrains.compose.web.dom.Text(". No file under ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Dockerfile")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Makefile")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("settings.gradle.kts")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("gradle.properties")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text(".github/")
        }
        org.jetbrains.compose.web.dom.Text(" or ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("scripts/")
        }
        org.jetbrains.compose.web.dom.Text(" changed at all.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-numbers") }) {
        org.jetbrains.compose.web.dom.Text("The numbers")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Measured on 2026-09-16, each on hardware named beside it, because a template that publishes an unmeasured number is the first thing its reader checks against their own clock.")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th()
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("git clone")
                    }
                    org.jetbrains.compose.web.dom.Text(" → both halves answering ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("/health/ready")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("3 min 48 s")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("of which the build, almost all Kotlin/Native toolchain")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("82 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("image, ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("gcr.io/distroless/cc-debian13")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("13 972 497 bytes")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("linuxX64")
                    }
                    org.jetbrains.compose.web.dom.Text(" binary inside it")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("9 228 056 bytes, declaring 7 shared libraries")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("resident at rest")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("14 136 kB")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("p95 at a delivered 499.95 req/s")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("1.86 ms")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("tests, from one source")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("23 on each of ")
                        org.jetbrains.compose.web.dom.Code {
                            org.jetbrains.compose.web.dom.Text("jvm")
                        }
                        org.jetbrains.compose.web.dom.Text(" and ")
                        org.jetbrains.compose.web.dom.Code {
                            org.jetbrains.compose.web.dom.Text("linuxX64")
                        }
                    }
                    org.jetbrains.compose.web.dom.Text(", the same binary run on arm64")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The first row is the one worth the qualification: it was taken in a fresh container with no Gradle cache, no Kotlin/Native toolchain and no Docker layers, cloning over the network — a machine that had never seen any of this. The image build is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("not")
        }
        org.jetbrains.compose.web.dom.Text(" in that figure, because the timing container had no Docker; separately it is about two minutes.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The latency row was taken on two hosts, subject and generator on separate machines over a private link, four runs of thirty seconds with the first discarded. On one machine the generator competes with the subject for CPU and the number describes the laptop rather than the service, which is why the tool refuses to write a result unless you name two hosts.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-ships-twice-actually-means") }) {
        org.jetbrains.compose.web.dom.Text("What \"ships twice\" actually means")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Not \"compiles for two targets\". Both halves run, and the interesting one is what happens when you stop them.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Send ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SIGTERM")
        }
        org.jetbrains.compose.web.dom.Text(" under load and every request already in flight finishes: 32 of 32, on each half, asserted from the client's record rather than from the server's log. Requests arriving after the announce get ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("503")
        }
        org.jetbrains.compose.web.dom.Text(" with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Connection: close")
        }
        org.jetbrains.compose.web.dom.Text(". The process ends itself — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("0")
        }
        org.jetbrains.compose.web.dom.Text(" on the native binary, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("143")
        }
        org.jetbrains.compose.web.dom.Text(" on the JVM, both correct — inside the grace period.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The two also answer identically. The same k6 scenario against both produces no diff after a normaliser that declares, before the first run, exactly what the platforms may disagree about: the exit code, the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Server")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Date")
        }
        org.jetbrains.compose.web.dom.Text(" headers, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/version")
        }
        org.jetbrains.compose.web.dom.Text("'s build timestamp. Anything it does not name is a difference that fails.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("before-you-clone") }) {
        org.jetbrains.compose.web.dom.Text("Before you clone")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("One thing is not true yet and it would be unfair to bury it.")
        }
        org.jetbrains.compose.web.dom.Text(" The libraries keel builds on are not on Maven Central, so a clone needs the repository they live in:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""// settings.gradle.kts, inside pluginManagement { repositories { … } }
maven("https://reposilite.kotlin.website/snapshots") {
    content { includeGroupByRegex("io\\.github\\.youndie.*") }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That block is in the template already. Until those artefacts reach Central, \"clone and run\" means \"clone and run, with that repository configured\" — which is most of the way there for me and not all the way there for you.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-deliberately-does-not-have") }) {
        org.jetbrains.compose.web.dom.Text("What it deliberately does not have")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No authentication: a template that ships half of one ships it to every clone. No Helm chart: that is a decision about a cluster the template does not have. No client. No second entity — the example is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Item { id, name }")
        }
        org.jetbrains.compose.web.dom.Text(" and it stays that way, because a starter whose example grows features stops being a starter.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And no static ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("scratch")
        }
        org.jetbrains.compose.web.dom.Text(" image, which the first draft of the plan called for. It would have saved about 4.5 MB on an image already 44 % under its budget, at the price of a recipe pinning five compiler properties that a Kotlin release can change. A convention that breaks on a version bump gets fixed once for everybody; ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("a template that breaks on one is copied and never updated again.")
        }
        org.jetbrains.compose.web.dom.Text(" The recipe is written down in the repository for anyone who wants it deliberately.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-the-interesting-parts-live") }) {
        org.jetbrains.compose.web.dom.Text("Where the interesting parts live")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("keel is thin on purpose — the lifecycle is ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/kore") {
            org.jetbrains.compose.web.dom.Text("kore")
        }
        org.jetbrains.compose.web.dom.Text(" and the build conventions are ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/sborka") {
            org.jetbrains.compose.web.dom.Text("sborka")
        }
        org.jetbrains.compose.web.dom.Text(", and this page is not about either. What keel does is show them wired, with a number beside every claim and a note beside every absence.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If you want the reasoning rather than the result, the repository carries it: a research document whose every fact names where it was verified, and a backlog where each closed item says what it found that nobody planned for. Several of those findings were defects in the libraries above, discovered by being their second consumer rather than their first.")
    }
}
